// material
import {
  Badge,
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import { filter } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';

//
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@iconify/react';

import {
  FlightListHead,
  FlightListToolbar,
  FlightListMoreMenu,
  FlightListImportFlight
} from '../components/flightList';
import Label from '../components/Label';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { ReportFilterSidebar } from '../components/sidebarFilter';
import { editItem, getReports, getReportsDeviceIds } from '../fetch';
import { reportTripsActions, sessionActions } from '../store';
import { useTranslation } from '../common/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import {
  selectAbonnementData,
  selectDevicesArray,
  selectGroupSelectedId,
  selectReportTripsArray,
  selectSession,
  selectUsersArray
} from '../createSelectorUtils';

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      array,
      (_reportTrip) =>
        (_reportTrip.startTime !== null &&
          moment(_reportTrip.startTime).format('DD/MM/YYYY HH:mm').indexOf(query.toLowerCase()) !==
            -1) ||
        (_reportTrip.driverName !== null &&
          _reportTrip.driverName.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.instructeurName !== null &&
          _reportTrip.instructeurName.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.deviceName !== null &&
          _reportTrip.deviceName.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.oaciAeroStart !== null &&
          _reportTrip.oaciAeroStart.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.oaciAeroEnd !== null &&
          _reportTrip.oaciAeroEnd.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.duration !== null &&
          `${Math.round(_reportTrip.duration / 60000)} min`.indexOf(query.toLowerCase()) !== -1) ||
        (_reportTrip.durationVol !== null &&
          `${Math.round(_reportTrip.durationVol / 60000)} min`.indexOf(query.toLowerCase()) !==
            -1) ||
        (_reportTrip.commentAffectationVols !== null &&
          _reportTrip.commentAffectationVols.toLowerCase().indexOf(query.toLowerCase()) !== -1)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function FlightList() {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSelector(selectSession);
  const abonnement = useSelector(selectAbonnementData);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const users = useSelector(selectUsersArray);

  const reportTrips = useSelector(selectReportTripsArray);
  const devices = useSelector(selectDevicesArray);

  const hiddenDown = useMediaQuery((theme) => theme.breakpoints.between('xs', 'sm'));

  const initialTableHead = useMemo(
    () => [
      {
        id: 'startTime',
        label: 'fields_Date',
        alignRight: false,
        display:
          (!session.attributes.displayFLStartTime &&
          session.attributes.displayFLStartTime !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'deviceName',
        label: 'fields_Device',
        alignRight: false,
        display:
          (!session.attributes.displayFLDeviceName &&
          session.attributes.displayFLDeviceName !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'oaciAeroStart',
        label: 'fields_Departure',
        alignRight: false,
        display:
          (!session.attributes.displayFLOaciAeroStar &&
          session.attributes.displayFLOaciAeroStar !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'oaciAeroEnd',
        label: 'fields_Arrival',
        alignRight: false,
        display:
          (!session.attributes.displayFLOaciAeroEnd &&
          session.attributes.displayFLOaciAeroEnd !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'driverName',
        label: 'fields_Pilot',
        alignRight: false,
        display:
          (!session.attributes.displayFLDriverName &&
          session.attributes.displayFLDriverName !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'instructeurName',
        label: 'fields_Instructor',
        alignRight: false,
        display:
          (!session.attributes.displayFLInstructeurName &&
          session.attributes.displayFLInstructeurName !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'duration',
        label: 'fields_Engine',
        alignRight: false,
        display:
          (!session.attributes.displayFLDuration &&
          session.attributes.displayFLDuration !== undefined
            ? 'none'
            : undefined) || undefined
      },
      {
        id: 'durationVol',
        label: 'fields_Movement',
        alignRight: false,
        display:
          (!session.attributes.displayFLDurationVol &&
          session.attributes.displayFLDurationVol !== undefined
            ? 'none'
            : undefined) || undefined
      },
      { id: '' }
    ],
    [
      session.attributes.displayFLDeviceName,
      session.attributes.displayFLDriverName,
      session.attributes.displayFLDuration,
      session.attributes.displayFLDurationVol,
      session.attributes.displayFLInstructeurName,
      session.attributes.displayFLOaciAeroEnd,
      session.attributes.displayFLOaciAeroStar,
      session.attributes.displayFLStartTime
    ]
  );
  const [tableHead, setTableHead] = useState(initialTableHead);

  const handleUpdateTableHead = async (newTableHead) => {
    setTableHead(newTableHead);

    if (!hiddenDown) {
      const body = {
        ...session,
        attributes: {
          ...session.attributes,
          displayFLStartTime: newTableHead[0].display === undefined,
          displayFLDeviceName: newTableHead[1].display === undefined,
          displayFLOaciAeroStar: newTableHead[2].display === undefined,
          displayFLOaciAeroEnd: newTableHead[3].display === undefined,
          displayFLDriverName: newTableHead[4].display === undefined,
          displayFLInstructeurName: newTableHead[5].display === undefined,
          displayFLDuration: newTableHead[6].display === undefined,
          displayFLDurationVol: newTableHead[7].display === undefined
        }
      };
      delete body.permission;

      const response = await editItem('users', session.id, body);
      if (response.ok) {
        enqueueSnackbar(t('profile_Changed'), {
          variant: 'success'
        });
        const result = await response.json();
        dispatch(sessionActions.update(await result));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  };

  useEffect(() => {
    if (hiddenDown) {
      const newTableHead = initialTableHead.map((item) =>
        item.id !== 'startTime' && item.id !== 'oaciAeroStart' && item.id !== 'oaciAeroEnd'
          ? { ...item, display: 'none' }
          : item
      );
      setTableHead(newTableHead);
    } else {
      setTableHead(initialTableHead);
    }
  }, [hiddenDown, initialTableHead]);

  // ----------------------------------------------------------------------

  const [page, setPage] = useState(
    reportTrips.length !== 0
      ? JSON.parse(window.localStorage.getItem('reportTripsFilterPage')) || 0
      : 0
  );
  const [order, setOrder] = useState(
    JSON.parse(window.localStorage.getItem('reportTripsFilterOrder')) || 'desc'
  );
  const [orderBy, setOrderBy] = useState(
    JSON.parse(window.localStorage.getItem('reportTripsFilterOrderBy')) || 'startTime'
  );
  const [filterName, setFilterName] = useState(
    JSON.parse(window.localStorage.getItem('reportTripsFilterName')) || ''
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    JSON.parse(window.localStorage.getItem('reportTripsFilterRowsPerPage')) || 10
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    window.localStorage.setItem('reportTripsFilterOrder', JSON.stringify(isAsc ? 'desc' : 'asc'));
    window.localStorage.setItem('reportTripsFilterOrderBy', JSON.stringify(property));
  };

  const handleChangePage = (event, newPage) => {
    window.localStorage.setItem('reportTripsFilterPage', JSON.stringify(newPage));
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    window.localStorage.setItem(
      'reportTripsFilterRowsPerPage',
      JSON.stringify(parseInt(event.target.value, 10))
    );
    window.localStorage.setItem('reportTripsFilterPage', JSON.stringify(0));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    window.localStorage.setItem('reportTripsFilterName', JSON.stringify(event.target.value));
    setPage(0);
  };

  // -------------------------------------------------------------------------

  const [openFilter, setOpenFilter] = useState(false);
  const [openProgress, setOpenProgress] = useState(false);

  const formik = useFormik({
    initialValues: {
      group: !session.administrator
        ? { id: groupId }
        : JSON.parse(window.localStorage.getItem('reportTripsFilterGroup')) || null,
      device: JSON.parse(window.localStorage.getItem('reportTripsFilterDevice')) || null,
      user: JSON.parse(window.localStorage.getItem('reportTripsFilterUser')) || null,
      period:
        JSON.parse(window.localStorage.getItem('reportTripsFilterPeriod')) ||
        (permission.status === 4 ? 'last3Month' : 'thisMonth'),
      from: window.localStorage.getItem('reportTripsFilterFrom')
        ? moment(new Date(window.localStorage.getItem('reportTripsFilterFrom')))
        : moment().subtract(1, 'day'),
      to: window.localStorage.getItem('reportTripsFilterTo')
        ? moment(new Date(window.localStorage.getItem('reportTripsFilterTo')))
        : moment(),
      assignFlight:
        JSON.parse(window.localStorage.getItem('reportTripsFilterAssignFlight')) || 'all',
      errorAnalyseFlight:
        JSON.parse(window.localStorage.getItem('reportTripsFilterErrorAnalyseFlight')) || false,
      flag: JSON.parse(window.localStorage.getItem('reportTripsFilterFlag')) || 'all',
      favorite: JSON.parse(window.localStorage.getItem('reportTripsFavorite')) || false,
      download: false,
      route: false,
      displayTaxiing: JSON.parse(window.localStorage.getItem('reportTripsDisplayTaxiing')) || false
    },
    onSubmit: async (values) => {
      setOpenProgress(true);
      let selectedFrom;
      let selectedTo;
      switch (values.period) {
        case 'today':
          selectedFrom = moment().startOf('day');
          selectedTo = moment().endOf('day');
          break;
        case 'yesterday':
          selectedFrom = moment().subtract(1, 'day').startOf('day');
          selectedTo = moment().subtract(1, 'day').endOf('day');
          break;
        case 'thisWeek':
          selectedFrom = moment().startOf('week');
          selectedTo = moment().endOf('week');
          break;
        case 'previousWeek':
          selectedFrom = moment().subtract(1, 'week').startOf('week');
          selectedTo = moment().subtract(1, 'week').endOf('week');
          break;
        case 'thisMonth':
          selectedFrom = moment().startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'previousMonth':
          selectedFrom = moment().subtract(1, 'month').startOf('month');
          selectedTo = moment().subtract(1, 'month').endOf('month');
          break;
        case 'last3Month':
          selectedFrom = moment().subtract(3, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'last6Month':
          selectedFrom = moment().subtract(6, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'thisYear':
          selectedFrom = moment().startOf('year');
          selectedTo = moment().endOf('month');
          break;
        default:
          selectedFrom = values.from.set('hour', 0).set('minute', 0).set('second', 0).utc(true);
          selectedTo = values.to.set('hour', 23).set('minute', 59).set('second', 59).utc(true);
          break;
      }
      window.localStorage.setItem('reportTripsFilterGroup', JSON.stringify(values.group));
      window.localStorage.setItem('reportTripsFilterDevice', JSON.stringify(values.device));
      window.localStorage.setItem('reportTripsFilterUser', JSON.stringify(values.user));
      window.localStorage.setItem('reportTripsFilterPeriod', JSON.stringify(values.period));
      window.localStorage.setItem(
        'reportTripsFilterFrom',
        moment(values.from, moment.HTML5_FMT.DATE)
      );
      window.localStorage.setItem('reportTripsFilterTo', moment(values.to, moment.HTML5_FMT.DATE));

      let response;
      if (
        groupId === 0 &&
        (values.group === null || values.group.id === 0) &&
        values.device === null
      ) {
        let deviceIds = '';
        devices
          .filter(
            (device) =>
              device.groupId === 0 ||
              session.permission.find((item) => item.groupid === device.groupId) === undefined
          )
          .map((item) => (deviceIds += `deviceId=${item.id}&`));
        response = await getReportsDeviceIds(
          values.route ? 'route' : 'trips',
          deviceIds,
          selectedFrom.toISOString(),
          selectedTo.toISOString(),
          values.download
        );
      } else {
        response = await getReports(
          values.route ? 'route' : 'trips',
          values.device !== null ? values.device.id : 0,
          values.group !== null && values.device === null ? values.group.id : 0,
          values.user !== null ? values.user.id : 0,
          selectedFrom.toISOString(),
          selectedTo.toISOString(),
          values.download
        );
      }

      if (!values.download) {
        if (response.ok) {
          setPage(0);
          window.localStorage.setItem('reportTripsFilterPage', JSON.stringify(0));
          const result = await response.json();
          result.sort((a, b) => moment(a.startTime) - moment(b.startTime));
          dispatch(reportTripsActions.update(result));
          if (result.length === 0) {
            enqueueSnackbar(t('FL_Empty_Snackbar'), {
              variant: 'warning'
            });
          }
        } else {
          dispatch(reportTripsActions.update([]));
          enqueueSnackbar(t('FL_Empty_Snackbar'), {
            variant: 'warning'
          });
        }
      } else if (values.download) {
        formik.setFieldValue('download', false);
        if (values.route) {
          formik.setFieldValue('route', false);
        }
      }

      setOpenFilter(false);
      setOpenProgress(false);
    }
  });
  const { resetForm, handleSubmit } = formik;

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    resetForm();
    window.localStorage.setItem('reportTripsFilterGroup', null);
    window.localStorage.setItem('reportTripsFilterDevice', null);
    window.localStorage.setItem('reportTripsFilterUser', null);
    window.localStorage.setItem('reportTripsFilterPeriod', JSON.stringify('today'));
    window.localStorage.setItem('reportTripsFilterFrom', moment().subtract(1, 'hour'));
    window.localStorage.setItem('reportTripsFilterTo', moment());
    window.localStorage.setItem('reportTripsFilterAssignFlight', null);
    window.localStorage.setItem('reportTripsFilterErrorAnalyseFlight', false);
    window.localStorage.setItem('reportTripsFilterFlag', null);
    window.localStorage.setItem('reportTripsFavorite', false);

    dispatch(reportTripsActions.update([]));
    handleCloseFilter();
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - reportTrips.length) : 0;

  const filteredReportTrips = applySortFilter(
    reportTrips,
    getComparator(order, orderBy),
    filterName
  )
    .filter((row) => {
      switch (formik.values.assignFlight) {
        case 'all':
          return true;
        case 'assigned':
          return row.affectationVolsId !== 0;
        case 'unassigned':
          return row.affectationVolsId === 0;
        default:
          return true;
      }
    })
    .filter((row) => {
      switch (formik.values.flag) {
        case 'all':
          return true;
        case 'signaler':
          return row.flagAffectationVols === 'signaler';
        case 'resolu':
          return row.flagAffectationVols === 'etude';
        case 'etude':
          return row.flagAffectationVols === 'resolu';
        case 'revoir':
          return row.flagAffectationVols === 'revoir';
        case 'concour':
          return row.flagAffectationVols === 'concour';
        default:
          return true;
      }
    })
    .filter((row) => {
      if (formik.values.errorAnalyseFlight) {
        return row.landing !== row.decollage || row.landing === 0 || row.decollage === 0;
      }
      return true;
    })
    .filter((row) => {
      if (formik.values.favorite) {
        return row.favoriteAffectationVols;
      }
      return true;
    })
    .filter((row) =>
      formik.values.displayTaxiing
        ? true
        : !(Math.round(row.durationVol / 60000) <= 10 && row.landing === 0)
    );

  const isReportTripNotFound = filteredReportTrips.length === 0;

  const handleClickTableRow = (deviceId, groupId, startTime, endTime, reportTrip) => {
    if (
      reportTrip.affectationVolsId !== 0 ||
      session.administrator ||
      formik.values.group.id === 0
    ) {
      const user = users.find((e) => e.id === reportTrip.driverUniqueId && session.id !== e.id) || {
        privateAccount: false
      };
      navigate('/flight/review', {
        state: {
          deviceId,
          groupId,
          startTime,
          endTime,
          privateAccount:
            user.privateAccount &&
            !session.administrator &&
            session.id !== reportTrip.driverUniqueId &&
            session.id !== reportTrip.instructeurUniqueId
        }
      });
    } else if (
      !(
        reportTrip.affectationVolsId !== 0 &&
        (permission.status === 2 || permission.status === 4) &&
        !session.administrator
      )
    ) {
      navigate('/flight/assign', {
        state: { deviceId, groupId, startTime, endTime }
      });
    }
  };

  // --------------------------------------------------------------
  useEffectAsync(async () => {
    if (
      formik.values.group !== null &&
      formik.values.group.id !== groupId &&
      !session.administrator
    ) {
      await handleResetFilter();
      formik.setFieldValue('group', { id: groupId });
      formik.setFieldValue(
        'user',
        JSON.parse(window.localStorage.getItem('reportTripsFilterUser')) || null
      );
    }
  }, [groupId]);

  useEffectAsync(async () => {
    if (reportTrips.length === 0) {
      await handleSubmit();
    }
  }, [devices.length]);

  // --------------------------------------------------------------
  return (
    <Page title={t('FL_Title')} openProgress={openProgress}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0}>
          <Typography variant="h4" gutterBottom>
            {t('flightsList')}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap-reverse"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 5 }}
        >
          <Stack direction="row" spacing={hiddenDown ? 0 : 1} flexShrink={0} sx={{ my: 1 }}>
            {(session.administrator || groupId === 0) && <FlightListImportFlight />}
            <Badge
              badgeContent={abonnement ? null : 'pro'}
              color="secondary"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <Button
                disableRipple
                color="inherit"
                endIcon={<Icon icon="fa-solid:file-download" />}
                onClick={() => {
                  if (abonnement) {
                    formik.setFieldValue('download', true);
                    handleSubmit();
                  }
                }}
                disabled={!abonnement}
              >
                {t('FL_Download')}
              </Button>
            </Badge>

            {session.administrator && (
              <Button
                disableRipple
                color="inherit"
                endIcon={<Icon icon="fa-solid:file-download" />}
                onClick={() => {
                  formik.setFieldValue('download', true);
                  formik.setFieldValue('route', true);
                  handleSubmit();
                }}
              >
                {hiddenDown ? '' : `${t('FL_Download')} route`}
              </Button>
            )}
            <ReportFilterSidebar
              formik={formik}
              isOpenFilter={openFilter}
              onResetFilter={handleResetFilter}
              onOpenFilter={handleOpenFilter}
              onCloseFilter={handleCloseFilter}
              onSaveFilter={handleSubmit}
              clubFilter
              deviceFilter
              userFilter
              tableHead={tableHead}
              handleUpdateTableHead={handleUpdateTableHead}
            />
          </Stack>
        </Stack>

        <Card sx={{ ml: hiddenDown ? -3 : 0, mr: hiddenDown ? -3 : 0 }}>
          <FlightListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 300 }}>
              <Table>
                <FlightListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  rowCount={reportTrips.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredReportTrips
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const {
                        deviceId,
                        startTime,
                        endTime,
                        deviceName,
                        driverName,
                        instructeurName,
                        oaciAeroStart,
                        oaciAeroEnd,
                        duration,
                        durationVol,
                        alertGeofence,
                        alertSpeed,
                        gAlert,
                        landing
                      } = row;
                      return (
                        <TableRow
                          hover
                          key={index}
                          sx={{
                            backgroundColor:
                              Math.round(durationVol / 60000) <= 10 && landing === 0
                                ? 'rgba(0, 0, 0, 0.1)'
                                : 'transparent'
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ display: tableHead[0].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                                {moment(startTime).format('DD/MM/YY HH:mm')}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[1].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            {deviceName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[2].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            <Label variant="ghost" color="info">
                              {oaciAeroStart}
                            </Label>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[3].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            <Label variant="ghost" color="success">
                              {oaciAeroEnd}
                            </Label>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[4].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            {driverName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[5].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            {instructeurName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[6].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            <Label variant="ghost" color="error">
                              {`${Math.round(duration / 60000)} min`}
                            </Label>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ display: tableHead[7].display }}
                            onClick={() =>
                              handleClickTableRow(
                                deviceId,
                                devices.find((el) => el.id === deviceId) &&
                                  devices.find((el) => el.id === deviceId).groupId,
                                startTime,
                                endTime,
                                row
                              )
                            }
                          >
                            <Label variant="ghost" color="secondary">
                              {`${Math.round(durationVol / 60000)} min`}
                            </Label>
                          </TableCell>
                          <TableCell size="small">
                            <Badge
                              badgeContent={alertGeofence + alertSpeed + gAlert}
                              color="error"
                              variant="dot"
                            >
                              <FlightListMoreMenu
                                deviceId={deviceId}
                                groupId={
                                  formik.values.group
                                    ? formik.values.group.id
                                    : (devices.find((el) => el.id === deviceId) &&
                                        devices.find((el) => el.id === deviceId).groupId) ||
                                      0
                                }
                                startTime={startTime}
                                endTime={endTime}
                                reportTrip={row}
                              />
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={9} />
                    </TableRow>
                  )}
                </TableBody>
                {isReportTripNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 50, 100, 250, 500]}
            component="div"
            count={filteredReportTrips.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
